import type { Component } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useToast } from '@ds/composables/useToast'

import translations from './useTheToast.translations'

const DEFAULT_DURATION = 10000
const DEFAULT_NAME = 'the-toast'

interface ToastParams {
  title?: string
  content?: string
  duration?: number
  name?: string
  closeCallback?: () => void
  actionLabel?: string
  actionCallback?: () => void
}

interface ToastParamsCustomIcon extends ToastParams {
  icon?: Component
}

export function useTheToast() {
  const i18n = useI18n()
  const toast = useToast()

  function closeToast(id: string) {
    toast.dismiss(id)
  }

  function closeAllToasts() {
    toast.dismissAll()
  }

  function openErrorToast(toastParams?: ToastParams): string {
    return toast.error(
      toastParams?.title || i18n(translations.defaultToastErrorTitle),
      {
        description:
          toastParams?.content || i18n(translations.defaultToastErrorContent),
        duration: toastParams?.duration || DEFAULT_DURATION,
        name: toastParams?.name || DEFAULT_NAME,
        actionLabel: toastParams?.actionLabel,
        onClose: toastParams?.closeCallback,
        onAction: toastParams?.actionCallback,
      },
    )
  }

  function openNeutralToast(
    // title and content are required for info toast, as there is no default translation
    toastParams: Required<Pick<ToastParamsCustomIcon, 'title' | 'content'>> &
      Omit<ToastParamsCustomIcon, 'title' | 'content'>,
  ): string {
    return toast.info(toastParams?.title, {
      description: toastParams?.content,
      duration: toastParams?.duration || DEFAULT_DURATION,
      name: toastParams?.name || DEFAULT_NAME,
      actionLabel: toastParams?.actionLabel,
      onClose: toastParams?.closeCallback,
      onAction: toastParams?.actionCallback,
      icon: toastParams?.icon,
    })
  }

  function openSuccessToast(toastParams?: ToastParams): string {
    return toast.success(
      toastParams?.title || i18n(translations.defaultToastSuccessTitle),
      {
        description:
          toastParams?.content || i18n(translations.defaultToastSuccessContent),
        duration: toastParams?.duration || DEFAULT_DURATION,
        name: toastParams?.name || DEFAULT_NAME,
        actionLabel: toastParams?.actionLabel,
        onClose: toastParams?.closeCallback,
        onAction: toastParams?.actionCallback,
      },
    )
  }

  return {
    openSuccessToast,
    openErrorToast,
    openNeutralToast,
    closeToast,
    closeAllToasts,
    TOAST_CONTENT_TELEPORT: '#toast-content',
  }
}
